import React from 'react';
import { useParams } from 'react-router-dom';

import useLediumPage from '@client/hooks/useLediumPage';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import lediumApi from '@client/utils/lediumApi';

import LightChannelsView from './LightChannelsView';

interface GroupInfo {
    id: string;
    name: string;
}

const LightChannels = () => {
    const {
        globalState: { selectedGroupId },
        globalActions
    } = useGlobalStore();
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const { pageType, itemId } = useParams<{ pageType?: string; itemId?: string }>();

    const { mainMenu, onSave } = useLediumPage({
        newItemDefaults: { zoneId: selectedGroupId },
        saveCondition: () => !!(formData.spectrogram as unknown[]).length,
        saveDataMap: () => ({
            name: formData.name,
            description: formData.description,
            resolution: [1, 8, 16, 24, 32].includes(formData.resolution as number) ? formData.resolution : 8,
            channel: +(formData.channel as string | number),
            spectrogram: formData.spectrogram,
            zoneId: formData.zoneId,
            csvData: formData.csvData,
            spectrogramRanges: formData.spectrogramRanges
        })
    });

    React.useEffect(() => {
        if (!formData.loaded) {
            return;
        }
        if (pageType !== 'new' && formData.zoneId && formData.zoneId !== selectedGroupId) {
            globalActions.zones.setSelectedGroup(formData.zoneId as string);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.loaded]);

    async function handleMoveZone(newZoneId: string, newZoneName: string) {
        const payload = {
            name: formData.name,
            description: formData.description,
            resolution: [1, 8, 16, 24, 32].includes(formData.resolution as number) ? formData.resolution : 8,
            channel: +(formData.channel as string | number),
            spectrogram: formData.spectrogram,
            zoneId: newZoneId,
            csvData: formData.csvData,
            spectrogramRanges: formData.spectrogramRanges
        };
        const result = await lediumApi.patch(`lightChannels/${itemId}`, payload);
        if (result) {
            const newGroup: GroupInfo = { id: newZoneId, name: newZoneName };
            pageActions.formData.updateFormItem({ zoneId: newZoneId, group: newGroup });
            globalActions.zones.setSelectedGroup(newZoneId);
        }
    }

    if (!formData.loaded) {
        return null;
    }

    const isEdit = pageType === 'edit';
    const currentGroup = formData.group as GroupInfo | null | undefined;

    return (
        <LightChannelsView
            onClose={mainMenu}
            onSave={onSave}
            onMoveZone={isEdit ? handleMoveZone : undefined}
            currentZoneId={isEdit ? (formData.zoneId as string | null) : undefined}
            currentZoneName={isEdit ? (currentGroup?.name ?? '') : undefined}
        />
    );
};

export default LightChannels;
